// The legacy course table uses icon components whose React 19 types are not
// compatible with the repository's TypeScript 6 JSX checker.
// @ts-nocheck
import { useToast } from "../../components/ui/Toast";
import { useEffect, useMemo, useState } from "react";
import { FilterBar } from "../../components/admin/dataTable/FilterBar";
import { useListState } from "../../components/admin/dataTable/useListState";
import { Link } from "react-router-dom";
import {
  BookOpen,
  ArrowRight,
  Loader2,
  Sparkles,
  FolderArchive,
  AlertTriangle,
  LayoutGrid,
  List,
  CheckCircle2,
} from "lucide-react";
import { api, type AdminCourseListItem } from "../../lib/api";
import { useAdminToken } from "./adminShared";
import { ConfirmDialog } from "../../components/admin/AdminModals";
import { StatusMessage, mapApiError } from "../../components/ui/StatusMessage";

export function AdminCoursesPage() {
  const { showToast } = useToast();
  const token = useAdminToken();
  const [courses, setCourses] = useState<AdminCourseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const list = useListState("c", ["access"], { key: "name", dir: "asc" });

  // Simple filter + sort. "Unassigned" always stays last.
  const shown = useMemo(() => {
    const real = courses.filter((c) => c.id !== "unassigned");
    const unassigned = courses.filter((c) => c.id === "unassigned");
    const pick = (c: AdminCourseListItem) => {
      switch (list.sortKey) {
        case "accessed": return c.totalAccess ?? 0;
        case "quiz": return c.quizAccess ?? 0;
        case "exam": return c.examAccess ?? 0;
        case "coc": return c.cocAccess ?? 0;
        default: return c.name.toLowerCase();
      }
    };
    const dir = list.sortDir === "asc" ? 1 : -1;
    const filtered = real.filter((c) => {
      const premium = Boolean(c.isPremium ?? c.is_premium);
      if (list.filters.access === "free") return !premium;
      if (list.filters.access === "premium") return premium;
      if (list.filters.access === "drafts") return (c.draftCount ?? 0) > 0;
      return true;
    });
    filtered.sort((a, b) => {
      const x = pick(a);
      const y = pick(b);
      if (typeof x === "number" && typeof y === "number") return (x - y) * dir || a.name.localeCompare(b.name);
      return String(x).localeCompare(String(y)) * dir;
    });
    return [...filtered, ...unassigned];
  }, [courses, list.sortKey, list.sortDir, list.filters.access]);

  // Premium toggle state
  const [premiumConfirm, setPremiumConfirm] = useState<{
    course: AdminCourseListItem;
    targetState: boolean;
    resourceCount: number;
  } | null>(null);
  const [togglingPremium, setTogglingPremium] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const fetchCourses = async (searchQuery?: string) => {
    if (!token) return;
    try {
      setLoading(true);
      setError(null);
      const res = await api.admin.listCourses(token, searchQuery);
      setCourses(res.items || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCourses(search);
    }, 250);
    return () => clearTimeout(timer);
  }, [token, search]);

  const handleInitiateTogglePremium = async (course: AdminCourseListItem) => {
    if (!token || course.id === "unassigned") return;
    const currentState = Boolean(course.isPremium ?? course.is_premium ?? false);
    const targetState = !currentState;
    try {
      const coursesApi = api.admin.catalog?.courses || api.admin.courses;
      const impact = await coursesApi.getSemesterImpact(token, course.id);
      setPremiumConfirm({
        course,
        targetState,
        resourceCount: impact.resourceCount ?? course.resourceCount ?? 0,
      });
    } catch {
      setPremiumConfirm({
        course,
        targetState,
        resourceCount: course.resourceCount ?? 0,
      });
    }
  };

  const handleConfirmTogglePremium = async () => {
    if (!token || !premiumConfirm) return;
    setTogglingPremium(true);
    try {
      const coursesApi = api.admin.catalog?.courses || api.admin.courses;
      await coursesApi.update(token, premiumConfirm.course.id, {
        is_premium: premiumConfirm.targetState,
      });
      setCourses((prev) =>
        prev.map((c) =>
          c.id === premiumConfirm.course.id
            ? { ...c, isPremium: premiumConfirm.targetState, is_premium: premiumConfirm.targetState }
            : c
        )
      );
      setNotice(
        `Course '${premiumConfirm.course.name}' marked as ${
          premiumConfirm.targetState ? "Premium" : "Free"
        }. All ${premiumConfirm.resourceCount} resources affected.`
      );
      setTimeout(() => setNotice(null), 4000);
      setPremiumConfirm(null);
    } catch (err) {
      showToast("error", mapApiError(err).message);
    } finally {
      setTogglingPremium(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-display font-bold tracking-tight text-foreground">Courses</h1>
        <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
          Manage course content, modules, past exams, quizzes, and review queues
        </p>
      </div>

      {/* Success Notice Banner */}
      {notice && (
        <div className="bg-success/15 border border-success/30 text-success text-xs font-semibold px-4 py-2.5 rounded-lg flex items-center gap-2">
          <CheckCircle2 size={15} className="shrink-0" />
          <span>{String(notice)}</span>
        </div>
      )}

      <div className="flex items-center gap-1 bg-secondary p-1 rounded-lg border border-border w-fit">
        <button
          type="button"
          onClick={() => setViewMode("table")}
          className={`p-1.5 rounded text-xs font-semibold flex items-center gap-1 transition ${
            viewMode === "table"
              ? "bg-card text-foreground shadow-xs border border-border"
              : "text-muted-foreground hover:text-foreground"
          }`}
          title="Table view"
        >
          <List size={14} />
          <span>Table</span>
        </button>
        <button
          type="button"
          onClick={() => setViewMode("grid")}
          className={`p-1.5 rounded text-xs font-semibold flex items-center gap-1 transition ${
            viewMode === "grid"
              ? "bg-card text-foreground shadow-xs border border-border"
              : "text-muted-foreground hover:text-foreground"
          }`}
          title="Cards"
        >
          <LayoutGrid size={14} />
          <span>Cards</span>
        </button>
      </div>

      <div className="relative max-w-md">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search courses by name or code…"
          className="field px-4 py-2 w-full text-sm rounded-md"
        />
      </div>

      <FilterBar
        list={list}
        filters={[{ key: "access", label: "Show", options: [{ value: "free", label: "Free" }, { value: "premium", label: "Premium" }, { value: "drafts", label: "Has drafts" }] }]}
        sortOptions={[
          { key: "name", label: "Name A to Z", dir: "asc" },
          { key: "name", label: "Name Z to A", dir: "desc" },
          { key: "accessed", label: "Most accessed", dir: "desc" },
          { key: "accessed", label: "Least accessed", dir: "asc" },
          { key: "quiz", label: "Most quiz use", dir: "desc" },
          { key: "quiz", label: "Least quiz use", dir: "asc" },
          { key: "exam", label: "Most past exam use", dir: "desc" },
          { key: "exam", label: "Least past exam use", dir: "asc" },
          { key: "coc", label: "Most COC use", dir: "desc" },
          { key: "coc", label: "Least COC use", dir: "asc" },
        ]}
      />

      {/* Content States */}
      {loading && (
        <div className="flex items-center justify-center p-16 text-muted-foreground text-sm">
          <Loader2 size={20} className="animate-spin mr-2 text-primary" /> Loading courses…
        </div>
      )}

      {error && !loading && (
        <div className="py-6">
          <StatusMessage
            {...mapApiError(error)}
            action={{ label: "Try again", onClick: () => fetchCourses(search) }}
          />
        </div>
      )}

      {!loading && !error && courses.length === 0 && (
        <div className="py-6">
          <StatusMessage
            variant="info"
            title="No courses found"
            message={search ? `No courses matching "${search}" were found.` : "No courses have been configured yet."}
            action={search ? { label: "Clear search", onClick: () => setSearch("") } : undefined}
          />
        </div>
      )}

      {!loading && !error && courses.length > 0 && viewMode === "table" && (
        <div className="bg-card rounded-lg border border-border overflow-hidden shadow-xs">
          <div className="overflow-x-auto admin-scroll-x">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground w-28">
                    Code
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Course Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Stream
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Semester
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Premium
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Resources
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Drafts
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-muted-foreground">
                    Access
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shown.map((course) => {
                  const isUnassigned = course.id === "unassigned";
                  const isCoursePremium = Boolean(course.isPremium ?? course.is_premium ?? false);

                  return (
                    <tr key={course.id} className="hover:bg-muted/30 transition-colors">
                      {/* Code */}
                      <td className="px-4 py-3 font-mono text-xs font-bold text-foreground">
                        {course.code || "COURSE"}
                      </td>

                      {/* Course Name */}
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/courses/${course.id}`}
                          className="font-semibold text-foreground hover:text-primary transition-colors inline-flex items-center gap-2"
                        >
                          <span>{course.name}</span>
                        </Link>
                        {course.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1 max-w-sm mt-0.5">
                            {course.description}
                          </p>
                        )}
                      </td>

                      {/* Stream */}
                      <td className="px-4 py-3">
                        {course.streamName ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                            {course.streamName}
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                            Common
                          </span>
                        )}
                      </td>

                      {/* Semester */}
                      <td className="px-4 py-3">
                        {course.semester ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border">
                            Semester {course.semester}
                          </span>
                        ) : (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30 inline-flex items-center gap-1">
                            <AlertTriangle size={11} /> No semester set
                          </span>
                        )}
                      </td>

                      {/* Premium Toggle */}
                      <td className="px-4 py-3">
                        {isUnassigned ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleInitiateTogglePremium(course)}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              isCoursePremium ? "bg-primary" : "bg-muted"
                            }`}
                            title={
                              isCoursePremium
                                ? "Course is Premium (all resources effectively premium). Click to turn off."
                                : "Course is Free (resource-level labels apply). Click to enable."
                            }
                          >
                            <span
                              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                                isCoursePremium ? "translate-x-4" : "translate-x-0"
                              }`}
                            />
                          </button>
                        )}
                      </td>

                      {/* Resource count */}
                      <td className="px-4 py-3 font-semibold text-foreground text-xs">
                        {course.resourceCount}
                      </td>

                      {/* Draft review count */}
                      <td className="px-4 py-3 text-xs">
                        {course.draftCount > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-warning/15 text-warning border border-warning/30">
                            <Sparkles size={11} />
                            {course.draftCount} to review
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </td>

                      {/* Access: total uses, breakdown on hover */}
                      <td
                        className="px-4 py-3 text-xs font-semibold text-foreground"
                        title={`Quizzes ${course.quizAccess ?? 0} · Past exams ${course.examAccess ?? 0} · COC ${course.cocAccess ?? 0}`}
                      >
                        {course.totalAccess ?? 0}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/admin/courses/${course.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                        >
                          <span>Manage</span>
                          <ArrowRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && courses.length > 0 && viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {shown.map((course) => {
            const isUnassigned = course.id === "unassigned";
            const isCoursePremium = Boolean(course.isPremium ?? course.is_premium ?? false);

            return (
              <div
                key={course.id}
                className={`group flex flex-col justify-between p-4 rounded-lg border transition shadow-xs ${
                  isUnassigned
                    ? "bg-secondary/40 border-dashed border-border hover:border-primary/50"
                    : "bg-card border-border hover:border-primary/50 hover:shadow-sm"
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${
                        isUnassigned
                          ? "bg-muted text-muted-foreground"
                          : "bg-primary/10 text-primary border border-primary/20"
                      }`}>
                        {isUnassigned ? <FolderArchive size={16} /> : <BookOpen size={16} />}
                      </div>
                      <div>
                        <span className="text-xs font-mono font-bold tracking-wider text-muted-foreground uppercase">
                          {course.code || "COURSE"}
                        </span>
                        <Link
                          to={`/admin/courses/${course.id}`}
                          className="text-sm font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1 block"
                        >
                          {course.name}
                        </Link>
                      </div>
                    </div>

                    {/* Draft review badge */}
                    {course.draftCount > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-warning/15 text-warning border border-warning/30 shrink-0">
                        <Sparkles size={11} />
                        {course.draftCount}
                      </span>
                    )}
                  </div>

                  {!isUnassigned && (
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      {course.streamName ? (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                          {course.streamName}
                        </span>
                      ) : (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                          Common
                        </span>
                      )}
                      {course.semester ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border">
                          Semester {course.semester}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-warning/15 text-warning border border-warning/30 flex items-center gap-1">
                          <AlertTriangle size={11} /> No semester set
                        </span>
                      )}
                    </div>
                  )}

                  {course.description && (
                    <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                      {course.description}
                    </p>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground">{course.resourceCount}</span>
                    <span>resources</span>
                  </div>

                  <div className="flex items-center gap-3">
                    {!isUnassigned && (
                      <div className="flex items-center gap-1.5" title="Toggle Course Premium status">
                        <span className="text-xs font-medium">Premium</span>
                        <button
                          type="button"
                          onClick={() => handleInitiateTogglePremium(course)}
                          className={`relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            isCoursePremium ? "bg-primary" : "bg-muted"
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                              isCoursePremium ? "translate-x-3" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>
                    )}
                    <Link
                      to={`/admin/courses/${course.id}`}
                      className="inline-flex items-center gap-1 text-primary font-semibold group-hover:translate-x-0.5 transition-transform"
                    >
                      <span>Manage</span>
                      <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation Dialog for Course Premium Toggle */}
      <ConfirmDialog
        open={Boolean(premiumConfirm)}
        title={
          premiumConfirm?.targetState
            ? `Set '${premiumConfirm.course.name}' as Premium?`
            : `Turn Off Premium on '${premiumConfirm?.course.name}'?`
        }
        message={
          premiumConfirm?.targetState
            ? `This course has ${premiumConfirm.resourceCount} resources. Setting this course to Premium will cause all its resources to effectively require a premium subscription. Individual resource labels are preserved and will be restored if turned off.`
            : `Turning off course premium will restore the individual Free / Premium labels for all ${premiumConfirm?.resourceCount} resources in this course.`
        }
        confirmText={premiumConfirm?.targetState ? "Make Course Premium" : "Turn Off Course Premium"}
        variant="primary"
        loading={togglingPremium}
        onConfirm={handleConfirmTogglePremium}
        onClose={() => setPremiumConfirm(null)}
      />
    </div>
  );
}
